// src/pages/Admin/create/shared/closingTime.ts
//
// Resolves a venue's closing time into a concrete deal end time.
//
// Everything here works on `datetime-local` wall-clock strings
// ("YYYY-MM-DDTHH:mm") in the venue's own timezone — the same strings the deal
// form holds and the payload builder converts on submit. No UTC conversion
// happens here, so there is no timezone math to get wrong.

import { RecurrenceDay } from "../../deals/api/useDeals";

export interface HourEntry {
  open: string;      // "HH:mm"
  close: string;     // "HH:mm"
  isClosed: boolean;
}

/** Hours arrive in two shapes in this codebase: the DB/admin API stores an
 *  array of `{day, open, close, isClosed}`, while useEditBusiness types it as a
 *  record keyed by day name. Accept either. */
export type BusinessHours =
  | Array<HourEntry & { day: string }>
  | Record<string, HourEntry>;

/** Index 0-6 matching JS `Date.getDay()` (0 = Sunday). */
const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const RECURRENCE_DAY_TO_NAME: Record<RecurrenceDay, string> = {
  sun: "sunday",
  mon: "monday",
  tue: "tuesday",
  wed: "wednesday",
  thu: "thursday",
  fri: "friday",
  sat: "saturday",
};

/** Both hour shapes flattened to `{ monday: {...}, ... }` with lowercased keys. */
export function normalizeHours(hours: BusinessHours | null | undefined): Record<string, HourEntry> {
  if (!hours) return {};
  if (Array.isArray(hours)) {
    const out: Record<string, HourEntry> = {};
    for (const entry of hours) {
      if (entry?.day) out[String(entry.day).toLowerCase()] = entry;
    }
    return out;
  }
  const out: Record<string, HourEntry> = {};
  for (const [day, entry] of Object.entries(hours)) {
    if (entry) out[day.toLowerCase()] = entry as HourEntry;
  }
  return out;
}

/** "HH:mm" → minutes since midnight, or null when unparseable. */
function toMinutes(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export interface ClosingResolution {
  /** datetime-local string for the deal's end, when resolvable. */
  endTime?: string;
  /** Why it could not be resolved — shown to the user, so keep it plain. */
  error?: string;
}

/**
 * The venue's closing time for the day the deal STARTS, as a concrete end.
 *
 * A close at or before the start belongs to the next calendar day: a Friday
 * 8:00 PM deal at a venue closing 1:00 AM ends 1:00 AM Saturday. A midnight
 * close ("00:00") is the same case and lands on the following day, which is why
 * existing deals read as "Wed 8:00 PM → 12:00 AM".
 *
 * `isClosed` wins over the stored times: some rows keep stale open/close values
 * on a day flagged closed (Trolley Pub's Tuesday is `isClosed: true` with
 * 09:00-02:00 still on it), and inventing a deal end from those would be wrong.
 */
export function resolveClosingEnd(
  startLocal: string,
  hours: BusinessHours | null | undefined,
): ClosingResolution {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(startLocal || "");
  if (!m) return { error: "Set a start time first." };

  const [year, month, day, sh, sm] = m.slice(1).map(Number);
  const startMinutes = sh * 60 + sm;

  const byDay = normalizeHours(hours);
  if (Object.keys(byDay).length === 0) return { error: "No hours on file for this venue." };

  // Local Date is safe here: it is only used to name the weekday and to add a
  // day, and the result is re-formatted as the same wall-clock string.
  const startDate = new Date(year, month - 1, day);
  const dayName = DAY_NAMES[startDate.getDay()];
  const entry = byDay[dayName];

  if (!entry) return { error: `No hours on file for ${dayName}.` };
  if (entry.isClosed) return { error: `This venue is closed on ${dayName}s.` };

  const closeMinutes = toMinutes(entry.close);
  if (closeMinutes === null) return { error: `Closing time for ${dayName} is not set.` };

  const endDate = new Date(startDate);
  if (closeMinutes <= startMinutes) endDate.setDate(endDate.getDate() + 1);

  const pad = (n: number) => String(n).padStart(2, "0");
  const endTime =
    `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}` +
    `T${entry.close.trim().padStart(5, "0")}`;

  return { endTime };
}

/**
 * The selected recurrence days whose closing times disagree.
 *
 * The end time is resolved ONCE, from the start day, and then baked into every
 * occurrence. That is correct for a single-weekday series and for any series
 * whose days share a closing time — but a Fri+Sat series at a venue closing
 * 1:00 AM Friday and 2:00 AM Saturday would carry Friday's time on both. This
 * reports the disagreement so the form can say so instead of failing silently.
 *
 * Returns [] when there is nothing to warn about (0-1 days, or all the same).
 */
export function conflictingClosingTimes(
  recurrenceDays: RecurrenceDay[],
  hours: BusinessHours | null | undefined,
): Array<{ day: string; close: string }> {
  if (!recurrenceDays || recurrenceDays.length < 2) return [];
  const byDay = normalizeHours(hours);

  const resolved = recurrenceDays.map((d) => {
    const name = RECURRENCE_DAY_TO_NAME[d];
    const entry = byDay[name];
    const close = !entry || entry.isClosed ? "closed" : entry.close?.trim() || "unset";
    return { day: name, close };
  });

  const distinct = new Set(resolved.map((r) => r.close));
  return distinct.size > 1 ? resolved : [];
}
